/**
 * Live Quiz (Kahoot-style) Types
 * Type definitions for real-time multiplayer quiz sessions
 */

import { LottieItem } from "@/lib/lottie-registry";

/**
 * Live quiz session status
 */
export type LiveQuizStatus = 
  | "lobby"           // Teams are joining and selecting mascots
  | "playing"         // Quiz is in progress, showing questions
  | "paused"          // Quiz paused (captain disconnected)
  | "section_break"   // Pause between sections
  | "showing_ranking" // Displaying current standings
  | "completed"       // Quiz finished
  | "cancelled";      // Quiz was cancelled

/**
 * Question phase within playing status
 * Used to track what screen to show during a question
 */
export type QuestionPhase = 
  | "countdown"       // "Get ready for question X" with 3-2-1 countdown
  | "active"          // Question is active, timer running
  | "results";        // Showing answer results, who was fastest, correct/wrong

/**
 * Countdown data for "Get ready" screen
 */
export interface CountdownData {
  questionNumber: number;
  totalQuestions: number;
  secondsRemaining: number;
  quizName?: string;
}

/**
 * Answer result for a single participant
 */
export interface ParticipantAnswerResult {
  participantId: string;
  teamName: string;
  mascotKey: string;
  selectedOption: number | null;
  isCorrect: boolean;
  timeTakenMs: number;
  pointsEarned: number;
  bonusEarned: number;
}

/**
 * Participant status
 */
export type ParticipantStatus = 
  | "waiting"       // In lobby, not ready
  | "ready"         // Clicked 'Klaar om te spelen'
  | "playing"       // Actively in quiz
  | "disconnected"  // Lost connection
  | "finished";     // Completed quiz

/**
 * Live quiz session (from quiz_live_sessions table)
 */
export interface LiveQuizSession {
  id: string;
  quiz_id: string;
  weekend_id: string | null;
  host_user_id: string;

  // Status
  status: LiveQuizStatus;

  // Current state
  current_question_index: number;
  current_section_index: number;
  question_started_at: string | null;
  question_ends_at: string | null;

  // Phase — authoritative UI state, derived by all clients from DB
  current_phase: QuestionPhase | null;
  countdown_started_at: string | null;
  answer_revealed: boolean;
  last_question_result: {
    results: QuestionResult;
    participant_results: ParticipantAnswerResult[];
  } | null;

  // Question flow
  questions_order: string[]; // Array of question IDs
  total_questions: number;

  // Timing
  timer_seconds: number;
  started_at: string | null;
  completed_at: string | null;

  // Pause state
  paused_at: string | null;
  paused_for_participant_id: string | null;
  time_remaining_when_paused: number | null;

  // Notification customization
  notification_title: string | null;
  notification_lottie: string | null;

  // Track if results were shown (for idempotent auto-show)
  results_shown_for_question_id: string | null;

  created_at: string;
  updated_at: string;

  // Joined data
  quiz?: Quiz;
  host?: { full_name: string; nickname: string | null };
  paused_for_participant?: LiveQuizParticipant;
}

/**
 * Quiz with sections and questions
 */
export interface Quiz {
  id: string;
  name: string;
  description: string | null;
  icon: string;
  color: string;
  is_live_mode: boolean;
  scheduled_at: string | null;
  timer_seconds: number;
  show_correct_after_each: boolean;
  weekend_id: string | null;
  
  // Questions grouped by section
  sections?: QuizSection[];
  questions?: QuizQuestion[];
}

/**
 * Quiz section for grouping questions
 */
export interface QuizSection {
  id: string;
  quiz_id: string;
  name: string;
  description: string | null;
  order_index: number;
  show_ranking_after: boolean;
  lottie_animation: string;
  questions?: QuizQuestion[];
}

/**
 * Quiz question
 */
export interface QuizQuestion {
  id: string;
  quiz_id: string | null;
  section_id: string | null;
  question: string;
  question_type: "multiple_choice" | "true_false";
  options: QuizOption[];
  correct_answer: string | null;
  explanation: string | null;
  difficulty: "easy" | "medium" | "hard";
  image_url: string | null;
  points: number;
  bonus_points_fast: number;
  time_limit_seconds: number;
  order_index: number;
}

/**
 * Quiz option
 */
export interface QuizOption {
  text: string;
  is_correct: boolean;
}

/**
 * Live quiz participant (from quiz_live_participants table)
 */
export interface LiveQuizParticipant {
  id: string;
  session_id: string;
  team_id: string;
  player_user_id: string;
  
  // Mascot
  mascot_key: string;
  
  // Status
  status: ParticipantStatus;
  is_ready: boolean;
  
  // Scores
  total_score: number;
  correct_answers: number;
  current_streak: number;
  best_streak: number;
  
  // Timing
  last_seen_at: string;
  joined_at: string;
  
  // Joined data
  team?: {
    id: string;
    name: string;
    color: string;
    avatar_url: string | null;
  };
  player?: {
    id: string;
    full_name: string;
    nickname: string | null;
    avatar_url: string | null;
  };
}

/**
 * Live quiz answer (from quiz_live_answers table)
 */
export interface LiveQuizAnswer {
  id: string;
  session_id: string;
  participant_id: string;
  question_id: string;
  selected_option_index: number | null;
  is_correct: boolean;
  answered_at: string;
  time_taken_ms: number;
  points_earned: number;
  bonus_earned: number;
}

/**
 * Live quiz spectator
 */
export interface LiveQuizSpectator {
  id: string;
  session_id: string;
  user_id: string;
  team_id: string | null;
  joined_at: string;
  last_seen_at: string;
  
  // Joined data
  user?: {
    full_name: string;
    nickname: string | null;
    avatar_url: string | null;
  };
}

/**
 * Ranking entry for leaderboard
 */
export interface RankingEntry {
  rank: number;
  participant: LiveQuizParticipant;
  total_score: number;
  correct_answers: number;
  average_time_ms: number;
  mascotLottie?: LottieItem;
}


/**
 * Question result after all answers
 */
export interface QuestionResult {
  question_id: string;
  correct_option_index: number;
  answer_counts: number[]; // Count per option
  fastest_correct: {
    participant_id: string;
    team_name: string;
    time_ms: number;
  } | null;
  points_awarded: {
    participant_id: string;
    points: number;
    bonus: number;
  }[];
}

/**
 * Admin control actions
 */
export type AdminAction = 
  | { type: "start_quiz" }
  | { type: "next_question" }
  | { type: "show_answer" }
  | { type: "show_ranking" }
  | { type: "start_section"; payload: { section_index: number } }
  | { type: "pause" }
  | { type: "resume" }
  | { type: "reassign_captain"; payload: { participant_id: string; new_user_id: string } }
  | { type: "end_quiz" };

/**
 * Team member for captain reassignment
 */
export interface TeamMemberOption {
  user_id: string;
  full_name: string;
  nickname: string | null;
  avatar_url: string | null;
  is_current_captain: boolean;
}

/**
 * Quiz Master (host) info
 */
export interface QuizMaster {
  user_id: string;
  full_name: string;
  avatar_url: string | null;
}

/**
 * Notification configuration for quiz start
 */
export interface QuizNotificationConfig {
  title: string;
  lottie_animation: string;
  send_push: boolean;
  /** The quiz master (host) who will control the quiz */
  quiz_master?: QuizMaster;
}

/**
 * Mascot selection state
 */
export interface MascotSelection {
  mascot_key: string;
  mascot: LottieItem;
  selected_by: string | null; // participant_id or null if available
}

/**
 * Points calculation (Kahoot-style)
 * Base: 500 points for correct answer
 * Speed bonus: Up to 500 points based on how fast you answer
 * Total max: 1000 points per question
 */
export const LIVE_QUIZ_POINTS = {
  BASE_CORRECT: 500,
  MAX_SPEED_BONUS: 500,
  TOTAL_MAX_PER_QUESTION: 1000,
} as const;

/**
 * Calculate points for a correct answer based on time
 * @param timeTakenMs - Time taken to answer in milliseconds
 * @param timerMs - Total time allowed in milliseconds
 * @returns { points: number, bonus: number }
 */
export const calculateLiveQuizPoints = (
  isCorrect: boolean,
  timeTakenMs: number,
  timerMs: number
): { points: number; bonus: number } => {
  if (!isCorrect) {
    return { points: 0, bonus: 0 };
  }
  
  // Calculate speed ratio (1 = instant, 0 = at time limit)
  const speedRatio = Math.max(0, 1 - (timeTakenMs / timerMs));
  
  const points = LIVE_QUIZ_POINTS.BASE_CORRECT;
  const bonus = Math.round(LIVE_QUIZ_POINTS.MAX_SPEED_BONUS * speedRatio);
  
  return { points, bonus };
};

